import React from "react";
import { BuilderState } from "./state";
import { EmailBuilderActions } from "./useEmailBuilder";
import { InspWrap, EmptyHint } from "./inspectorStyled";
import { ElementPanel, BlockPanel, ThemePanel } from "./inspectorPanels";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface InspectorProps {
	state: BuilderState;
	actions: EmailBuilderActions;
}

// ---------------------------------------------------------------------------
// Inspector — routing layer only
// Decides which panel(s) to show; delegates all rendering to inspectorPanels.
// ---------------------------------------------------------------------------

/** Row-bound element keys: "blockId:FIELD:index" where FIELD is one of these. */
const ROW_EL_FIELDS = ["rlbl", "rval", "rlbl2", "rval2", "rraw", "nk", "nv", "nli", "fline"];

/** Parse selEl into a row reference, or null when the element is not a row field. */
function parseRowSel(selEl: string | null): { blockId: string; index: number } | null {
	if (!selEl) return null;
	const parts = selEl.split(":");
	if (parts.length < 3) return null;
	if (!ROW_EL_FIELDS.includes(parts[1])) return null;
	const index = parseInt(parts[2], 10);
	if (isNaN(index)) return null;
	return { blockId: parts[0], index };
}

const rowBtnStyle: React.CSSProperties = {
	display: "flex",
	alignItems: "center",
	gap: 6,
	width: "100%",
	padding: "8px 10px",
	border: "1px solid #e3e8ef",
	borderRadius: 8,
	background: "#fff",
	cursor: "pointer",
	fontSize: 12.5,
	fontWeight: 600,
	color: "#334155",
};

/** Per-row actions (delete / duplicate / merge / split the selected line inside summary/notes/footer). */
function RowActionsPanel({
	row,
	state,
	actions,
}: {
	row: { blockId: string; index: number };
	state: BuilderState;
	actions: EmailBuilderActions;
}): JSX.Element {
	// Determine if merge is applicable: current row and next row must both be plain single summary rows.
	const block = state.blocks.find((b) => b.id === row.blockId);
	const isSummary = block?.type === "summary";
	const rows = isSummary ? (block!.data.rows as import("../blockModel/types").SummaryRow[]) : [];
	const currentRow = rows[row.index];
	const nextRow = rows[row.index + 1];
	const canMerge =
		isSummary &&
		!!currentRow &&
		!!nextRow &&
		currentRow.raw === undefined &&
		nextRow.raw === undefined &&
		currentRow.label2 === undefined &&
		nextRow.label2 === undefined;
	const canSplit = isSummary && !!currentRow && currentRow.label2 !== undefined;

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
			<div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", color: "#94a3b8" }}>
				DÒNG ĐANG CHỌN
			</div>
			<button
				type="button"
				style={rowBtnStyle}
				data-testid="row-dup"
				onClick={() => actions.dupRow(row.blockId, row.index)}>
				<i
					className="material-icons"
					style={{ fontSize: 15, color: "#005e9e" }}>
					content_copy
				</i>
				Nhân đôi dòng
			</button>
			{canMerge && (
				<button
					type="button"
					style={rowBtnStyle}
					data-testid="row-merge"
					onClick={() => {
						actions.mergeRow(row.blockId, row.index);
						actions.setSelEl(null);
					}}>
					<i
						className="material-icons"
						style={{ fontSize: 15, color: "#7c3aed" }}>
						view_column
					</i>
					Ghép với dòng dưới
				</button>
			)}
			{canSplit && (
				<button
					type="button"
					style={rowBtnStyle}
					data-testid="row-split"
					onClick={() => {
						actions.splitRow(row.blockId, row.index);
						actions.setSelEl(null);
					}}>
					<i
						className="material-icons"
						style={{ fontSize: 15, color: "#0e7490" }}>
						vertical_split
					</i>
					Tách 2 cột
				</button>
			)}
			<button
				type="button"
				style={{ ...rowBtnStyle, color: "#dc2626", borderColor: "#fecaca" }}
				data-testid="row-del"
				onClick={() => {
					actions.delRow(row.blockId, row.index);
					actions.setSelEl(null);
				}}>
				<i
					className="material-icons"
					style={{ fontSize: 15 }}>
					delete
				</i>
				Xóa dòng này
			</button>
		</div>
	);
}

export function Inspector({ state, actions }: InspectorProps): JSX.Element {
	const selectedBlock = state.selId ? state.blocks.find((b) => b.id === state.selId) : null;
	const rowSel = parseRowSel(state.selEl);

	return (
		<InspWrap>
			{/* Row actions — when the selected element is a line inside a rows-block */}
			{rowSel ? (
				<RowActionsPanel
					row={rowSel}
					state={state}
					actions={actions}
				/>
			) : null}

			{/* Element panel — highest priority when an element is selected */}
			{state.selEl ? (
				<ElementPanel
					selEl={state.selEl}
					state={state}
					actions={actions}
				/>
			) : selectedBlock ? (
				/* Block panel — shown when a block (not a specific element) is selected */
				<BlockPanel
					block={selectedBlock}
					actions={actions}
				/>
			) : (
				/* Idle state */
				<EmptyHint>Bấm vào một chữ hoặc khối trong email để chỉnh thuộc tính.</EmptyHint>
			)}

			{/* Theme panel — always visible at the bottom */}
			<ThemePanel
				theme={state.theme}
				actions={actions}
			/>
		</InspWrap>
	);
}
